<template>
    <v-container class="pa-5" fluid>
        <CodeReaderDialog
            :is-open="codeReaderDialog"
            :settings="codeReaderSettings"
            @code-stream-start="showCodeReaderDialog"
            @code-stream-stop="hideCodeReaderDialog"
            @code-stream-handle="handleQrJSON"
        />

        <WorkspaceSettingsDialog
            :is-open="workspaceSettingsDialog"
            :is-persistent="workspaceSettingsDialogPersistent"
            :workspaces="workspaces"
            :workspace-id-data="workspaceId"
            @on-save="saveWorkspaceSettings"
            @on-abort="closeWorkspaceSettingsDialog"
        />

        <PinConfirmationDialog
            :is-open="pinConfirmationDialog"
            :pin-data="pin"
            @confirm-pin="confirmPin"
            @on-close="closePinConfirmationDialog"
        />

        <PackageAmountDialog
            :is-open="packageAmountDialog"
            :address-data="currentAddress"
            @update-sending="updateSendingParameters"
            @on-close="closePackageAmountDialog"
        />

        <ParcelPackagesAmountDialog
            :is-open="parcelPackagesAmountDialog"
            :parcel-data="parcel"
            @update-parcel="updateParcel"
            @on-close="closeParcelPackagesAmountDialog"
        />

        <AmountToShipDialog
            :is-open="amountToShipDialog"
            :address-data="currentAddress"
            @update-sending="updateSendingParameters"
            @reset-recalculate-unit-box="resetRecalculateUnitBox"
            @on-close="closeAmountToShipDialog"
        />

        <CourierDialog
            :is-open="courierDialog"
            :address-data="currentAddress"
            :couriers="couriers"
            @update-sending="updateSendingParameters"
            @on-close="closeCourierDialog"
        />

        <ParcelCourierDialog
            :is-open="parcelCourierDialog"
            :parcel-data="parcel"
            :couriers="couriers"
            @update-parcel="updateParcel"
            @on-close="closeParcelCourierDialog"
        />

        <ReprintLabelsDialog
            :is-open="reprintLabelsDialog"
            :address-data="currentAddress"
            @reprint-labels="reprintLabels"
            @on-close="closeReprintLabelsDialog"
        />

        <ReprintPackageLabelsDialog
            :is-open="reprintPackageLabelsDialog"
            :parcel-data="parcel"
            @reprint-package-labels="reprintPackageLabels"
            @on-close="closeReprintPackageLabelsDialog"
        />

        <ImagePreviewDialog
            :is-open="imagePreviewDialog"
            :file-preview-uri="filePreviewURI"
            @on-close="closeImagePreviewDialog"
        />

        <CouriersListDialog
            :is-open="couriersListDialog"
            :couriers-data="couriers"
            @on-close="closeCouriersListDialog"
        />

        <CreateAccidentDialog
            :accident="accident"
            :is-open="createAccidentDialog"
            @create-accident="createAccident"
            @close-accident-dialog="closeCreateAccidentDialog"
        />

        <CreateShipmentProcessReportDialog
            :is-open="createShipmentProcessReportDialog"
            :report-processes-data="reportProcesses"
            @on-close="closeCreateShipmentProcessReportDialog"
        />

        <ConfirmDialog
            :is-open="confirmStopReportingSessionDialog"
            :action-loading="actionLoading.reportingSession"
            @on-confirm="stopReportingSession"
            @on-abort="closeConfirmStopReportingSessionDialog"
        >
            <template #title>
                Zakończenie dziennego raportu
            </template>
            <template #content>
                Czy na pewno chcesz zakończyć pracę?
            </template>
            <template #confirmButton>
                Zakończ
            </template>
        </ConfirmDialog>
        <ConfirmDialog
            :is-open="showConfirmSendPackage"
            @on-confirm="confirmSendPackage"
            @on-abort="closeConfirmSendPackageDialog"
        >
            <template #title>
                Nadawanie paczki
            </template>
            <template #content>
                Czy chcesz nadać paczkę?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>

        <PackageDialog
            :is-open="packageDialog"
            :package-data="currentPackage"
            :box-types-data="boxTypes"
            :package-types-data="packageTypes"
            :boxes-info="boxesInfo"
            :parcel-data="parcel"
            @on-close="closePackageDialog"
            @on-save="savePackage"
        />

        <ConfirmDialog
            :is-open="confirmDeletePackageDialog"
            :data="currentPackage"
            @on-confirm="deletePackage"
            @on-abort="closeConfirmDeletePackageDialog"
        >
            <template #title>
                Usuwanie pakowania
            </template>
            <template #content>
                Czy na pewno chcesz usunąć pakowanie?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmDeletePalletDialog"
            :data="currentPallet"
            @on-confirm="deletePallet"
            @on-abort="closeConfirmDeletePalletDialog"
        >
            <template #title>
                Usuwanie palety
            </template>
            <template #content>
                Czy na pewno chcesz usunąć paletę?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <ShipmentReportShiftSummaryDialog
            :is-open="shipmentReportShiftSummaryDialog"
            :report-data="currentReportShiftSummary"
            :action-loading="actionLoading.approveReport"
            :stopped-reporting-session="stoppedReportingSession"
            @on-close="closeShipmentReportShiftSummaryDialog"
        />

        <v-row>
            <v-col cols="12">
                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="12" lg="9">
                            <h2 class="text-h6">
                                Witaj {{ userName }}
                            </h2>

                            <p class="mt-2">
                                Dziś jest {{ currentDate }}
                            </p>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="3" xl="3">
                            <v-card>
                                <v-toolbar flat dense color="grey lighten-4">
                                    <v-container class="pa-0">
                                        <v-row justify="space-between" dense align="center">
                                            <v-col>
                                                <v-toolbar-title class="text-subtitle-1">
                                                    Stanowisko
                                                </v-toolbar-title>
                                            </v-col>
                                            <v-col cols="auto">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            v-if="canChangeWorkspace"
                                                            fab
                                                            x-small
                                                            color="secondary"
                                                            elevation="1"
                                                            :loading="actionLoading.workspaces"
                                                            v-on="on"
                                                            @click="showWorkspaceSettingsDialog(false)"
                                                        >
                                                            <v-icon>
                                                                mdi-printer-3d
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Zmiana stanowiska</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-toolbar>

                                <v-divider />

                                <v-card-text class="py-8 text-center">
                                    <div class="text-h3 font-weight-bold black--text">
                                        {{ currentWorkspace ? currentWorkspace.name : '-' }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-divider class="my-4" />

                <v-container class="pa-0">
                    <v-row justify="center">
                        <v-col cols="12" md="8">
                            <v-btn
                                v-if="!isReportingSessionActive"
                                color="success"
                                block
                                x-large
                                class="mb-1"
                                data-sentry-label="Rozpoczynanie pracy"
                                :loading="actionLoading.reportingSession"
                                @click="startReportingSession"
                            >
                                Rozpoczynam pracę
                            </v-btn>

                            <v-btn
                                v-if="isReportingSessionActive"
                                color="error"
                                block
                                x-large
                                class="mb-1"
                                data-sentry-label="Kończenie pracy"
                                @click="showConfirmStopReportingSessionDialog"
                            >
                                Kończę na dziś
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-fade-transition>
                        <v-form v-if="isReportingSessionActive" @submit.prevent="getOrder()">
                            <v-container fluid class="pa-0">
                                <v-row align="center">
                                    <v-col cols="auto" sm="5" lg="3">
                                        <v-text-field
                                            v-model="orderNumber"
                                            label="Numer zamówienia"
                                            solo
                                            hide-details
                                            clearable
                                        />
                                    </v-col>

                                    <v-col cols="auto">
                                        <v-btn
                                            color="primary"
                                            fab
                                            small
                                            elevation="1"
                                            type="submit"
                                            data-sentry-label="Szukaj"
                                            :disabled="!orderNumber"
                                            :loading="actionLoading.getOrder || QRLoading"
                                        >
                                            <v-icon>
                                                mdi-magnify
                                            </v-icon>
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-menu bottom right>
                                            <template #activator="{on}">
                                                <v-btn
                                                    color="secondary"
                                                    fab
                                                    small
                                                    elevation="1"
                                                    :loading="actionLoading.createAccident"
                                                    v-on="on"
                                                >
                                                    <v-icon>
                                                        mdi-dots-vertical
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list>
                                                <v-list-item
                                                    @click="showCouriersListDialog"
                                                >
                                                    <v-list-item-action>
                                                        <v-icon>
                                                            mdi-truck-fast
                                                        </v-icon>
                                                    </v-list-item-action>
                                                    <v-list-item-title>
                                                        <template>
                                                            Przewoźnicy
                                                        </template>
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item
                                                    @click="showCreateAccidentDialog"
                                                >
                                                    <v-list-item-action>
                                                        <v-icon>
                                                            mdi-alert
                                                        </v-icon>
                                                    </v-list-item-action>
                                                    <v-list-item-title>Zgłoszenie awarii</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </v-col>
                                    <v-col>
                                        <v-btn
                                            color="primary"
                                            outlined
                                            class="ml-4"
                                            @click="showCreateShipmentProcessReportDialog"
                                        >
                                            Raportuj czynność
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-btn
                                            v-if="hasCamera"
                                            text
                                            icon
                                            color="primary"
                                            class="hidden-md-and-up ml-2"
                                            data-sentry-label="Otwórz czytnik kodów"
                                            @click="showCodeReaderDialog({
                                                expectedQrTypeCodes: ['KP'],
                                                origin: null
                                            })"
                                        >
                                            <v-icon>mdi-camera</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-form>
                    </v-fade-transition>
                    <v-fade-transition v-if="!showPackageComponent">
                        <v-container v-if="isReportingSessionActive && !isOrderFound" fluid class="pa-0">
                            <v-row>
                                <v-col cols="12">
                                    <v-card>
                                        <v-card-text>
                                            <v-alert
                                                type="info"
                                                text
                                                border="left"
                                            >
                                                Nie odnaleziono zamówienia.
                                            </v-alert>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-container v-if="isReportingSessionActive && isOrderFound" fluid class="pa-0">
                            <v-row>
                                <v-col cols="12">
                                    <v-alert
                                        v-if="order.shippingBlocked"
                                        type="info"
                                        class="mb-0"
                                        elevation="2"
                                        text
                                        border="left"
                                        icon="mdi-information-outline"
                                    >
                                        Wysyłka zablokowana. Zostanie wygenerowana etykieta tymczasowa.
                                    </v-alert>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" lg="9">
                                    <v-btn
                                        v-if="hasComments && !areCommentsVisible"
                                        color="error"
                                        outlined
                                        x-large
                                        block
                                        class="mb-3"
                                        data-sentry-label="Pokaż uwagi do zamówienia"
                                        @click="areCommentsVisible = true"
                                    >
                                        Pokaż uwagi do zamówienia
                                    </v-btn>

                                    <v-alert
                                        v-if="hasComments"
                                        v-model="areCommentsVisible"
                                        type="error"
                                        prominent
                                        dark
                                        elevation="2"
                                    >
                                        <span class="text-uppercase">Uwagi do zamówienia</span>

                                        <v-divider
                                            class="my-4"
                                            color="error"
                                        />

                                        <div v-for="(comment, index) in order.orderComments" :key="index">
                                            {{ index + 1 }}. {{ comment }}
                                        </div>

                                        <v-divider
                                            class="my-4"
                                            color="error"
                                        />
                                        <v-btn
                                            depressed
                                            outlined
                                            large
                                            block
                                            class="mt-5"
                                            data-sentry-label="Potwierdzenie zapoznania się z komentarzem"
                                            @click="confirmCommentsRead()"
                                        >
                                            Potwierdzam zapoznanie się z komentarzem
                                        </v-btn>
                                    </v-alert>
                                    <v-alert
                                        v-if="isCashOnDeliveryPartialSent && !isStandardCooperantOrder"
                                        type="warning"
                                        icon="mdi-alert"
                                        border="left"
                                        class="alertText"
                                    >
                                        Zamówienie nadane za pobraniem. Jeśli chcesz je ponownie nadać zrób to ręcznie.
                                    </v-alert>
                                    <v-alert
                                        v-if="isStandardCooperantOrder"
                                        type="warning"
                                        icon="mdi-alert"
                                        border="left"
                                        class="alertText"
                                    >
                                        To jest zamówienie wykonywane w kooperacji. Nadanie na linii nie jest możliwe.
                                    </v-alert>
                                    <v-card>
                                        <v-carousel
                                            v-model="addressCarouselIndex"
                                            height="auto"
                                            hide-delimiters
                                            :continuous="false"
                                            :dark="$vuetify.theme.dark"
                                            :light="!$vuetify.theme.dark"
                                            :show-arrows="order.addresses.length > 1"
                                        >
                                            <v-carousel-item
                                                v-for="(address, index) in order.addresses"
                                                :key="address.id"
                                            >
                                                <v-toolbar
                                                    :dark="address.orderAddress.isSent"
                                                    flat
                                                    height="30"
                                                    :color="address.orderAddress.isSent ? 'success' : 'grey lighten-2'"
                                                >
                                                    <v-row justify="center">
                                                        Adres {{ index + 1 }} z {{ order.addresses.length }}
                                                    </v-row>
                                                </v-toolbar>
                                                <v-container class="px-10">
                                                    <v-row>
                                                        <v-col>
                                                            <h2 class="text-h5 mb-4">
                                                                {{ formatAddressHeader(address) }}
                                                            </h2>

                                                            <div class="d-inline-flex mr-3">
                                                                <p class="my-0">
                                                                    Status:
                                                                    <strong :class="orderAddressSendStatusClass(index)">
                                                                        {{ address.orderAddress.orderAddressStatus.name }}
                                                                    </strong>
                                                                </p>
                                                            </div>

                                                            <div class="d-inline-flex ml-3 mr-2">
                                                                <p class="d-flex align-center my-0">
                                                                    {{ packageTypeName(address.orderAddress.deliveryType) }}: <strong class="mx-1">{{ address.orderAddress.packageAmountSum }}</strong>
                                                                    <v-btn
                                                                        class="ml-1"
                                                                        icon
                                                                        color="primary"
                                                                        small
                                                                        data-sentry-label="Edytuj ilość kartonów"
                                                                        @click="canApplyChangesWithoutPin ? showPackageAmountDialog(currentAddress) : showPinDialog('packageAmount')"
                                                                    >
                                                                        <v-icon small>
                                                                            mdi-pencil
                                                                        </v-icon>
                                                                    </v-btn>
                                                                </p>
                                                            </div>

                                                            <div class="d-inline-flex mx-2">
                                                                <p class="d-flex align-center my-0">
                                                                    Nakład: <strong class="mx-1">{{ address.orderAddress.amountToShip }} szt.</strong>
                                                                    <v-btn
                                                                        class="ml-1"
                                                                        icon
                                                                        color="primary"
                                                                        small
                                                                        data-sentry-label="Edytuj nakład"
                                                                        @click="showAmountToShipDialog(currentAddress)"
                                                                    >
                                                                        <v-icon small>
                                                                            mdi-pencil
                                                                        </v-icon>
                                                                    </v-btn>
                                                                </p>
                                                            </div>

                                                            <div class="d-inline-flex ml-2 mr-6">
                                                                <p class="my-0">
                                                                    Waga: <strong>{{ formatWeight(address.orderAddress.weight) }}</strong>
                                                                </p>
                                                            </div>

                                                            <div class="d-inline-flex">
                                                                <p class="d-flex align-center">
                                                                    Kurier: <strong class="mx-1">{{ address.orderAddress.carrier.shortCode }} ({{ address.orderAddress.carrier.deliveryTypeDescription }})</strong>
                                                                    <v-btn 
                                                                        v-if="!address.orderAddress.isPallet"
                                                                        class="ml-1"
                                                                        icon
                                                                        color="primary"
                                                                        small
                                                                        data-sentry-label="Zmień kuriera"
                                                                        @click="canApplyChangesWithoutPin ? showCourierDialog(currentAddress) : showPinDialog('courier')"
                                                                    >
                                                                        <v-icon small>
                                                                            mdi-pencil
                                                                        </v-icon>
                                                                    </v-btn>
                                                                </p>
                                                            </div>
                                                            <v-container v-if="address.orderAddress.isPallet" class="pa-0 mt-5">
                                                                <v-row style="max-width:800px">
                                                                    <v-col cols="12">
                                                                        <v-card>
                                                                            <v-list>
                                                                                <v-list-item>
                                                                                    <v-list-item-content>
                                                                                        <v-row no-gutters dense>
                                                                                            <v-col cols="12" sm="2" class="pa-2 d-flex justify-center">
                                                                                                <strong>Numer palety</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3" class="pa-2 d-flex justify-center">
                                                                                                <strong>Paleta</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3" class="pa-2 d-flex justify-center">
                                                                                                <strong>Nakład</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3" class="pa-2 d-flex justify-center">
                                                                                                <strong>Liczba kartonów na palecie</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="1" class="pa-2 d-flex justify-center" />
                                                                                        </v-row>
                                                                                    </v-list-item-content>
                                                                                </v-list-item>
                                                                                <v-divider />
                                                                                <v-list-item v-for="(box, boxIndex) in address.orderAddress.boxesInfo" :key="boxIndex">
                                                                                    <v-list-item-content>
                                                                                        <v-row no-gutters align="center" dense>
                                                                                            <v-col cols="12" sm="2" class="d-flex justify-center align-center">
                                                                                                {{ boxIndex + 1 }}
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3" class="pa-2">
                                                                                                <v-select
                                                                                                    :items="packagesPalettList"
                                                                                                    :value="box"
                                                                                                    :disabled="order.isSent"
                                                                                                    item-text="name"
                                                                                                    item-value="id"
                                                                                                    label="Paleta"
                                                                                                    :hide-selected="true"
                                                                                                    outlined
                                                                                                    single-line
                                                                                                    dense
                                                                                                    style="max-height:40px"
                                                                                                />
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3" class="pa-2">
                                                                                                <v-text-field
                                                                                                    v-model="box.singleBoxProductsAmount"
                                                                                                    :disabled="order.isSent"
                                                                                                    label="Nakład"
                                                                                                    outlined
                                                                                                    single-line
                                                                                                    dense
                                                                                                    :error="box.singleBoxProductsAmount && box.singleBoxProductsAmount <= 0 || isNaN(box.singleBoxProductsAmount)"
                                                                                                    style="max-height:40px"
                                                                                                    @input="calculateTotalAmountUsed"
                                                                                                />                                           
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3" class="pa-2">
                                                                                                <v-text-field
                                                                                                    v-model="box.packageAmount"
                                                                                                    :disabled="order.isSent"
                                                                                                    label="Liczba kartonów na palecie"
                                                                                                    outlined
                                                                                                    single-line
                                                                                                    dense
                                                                                                    :error="box.packageAmount && box.packageAmount <= 0 || isNaN(box.packageAmount)"
                                                                                                    style="max-height:40px"
                                                                                                    @input="calculateTotalAmountUsed"
                                                                                                />
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="1" class="pa-2">
                                                                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                                                                    <template #activator="{on}">
                                                                                                        <v-btn
                                                                                                            v-if="!order.isSent"
                                                                                                            icon
                                                                                                            data-sentry-label="Potwierdzenie usunięcia palety"
                                                                                                            v-on="on"
                                                                                                            @click="showConfirmDeletePalletDialog(address.orderAddress.boxesInfo, boxIndex)"
                                                                                                        >
                                                                                                            <v-icon color="pink">
                                                                                                                mdi-delete
                                                                                                            </v-icon>
                                                                                                        </v-btn>
                                                                                                    </template>
                                                                                                    <span>Usuń</span>
                                                                                                </v-tooltip>
                                                                                            </v-col>
                                                                                        </v-row>
                                                                                    </v-list-item-content>
                                                                                </v-list-item>
                                                                                <v-divider />
                                                                            </v-list>
                                                                            <v-col cols="12" class="d-flex justify-center">
                                                                                <v-btn
                                                                                    v-if="!order.isSent"
                                                                                    icon
                                                                                    outlined
                                                                                    rounded                               
                                                                                    fab
                                                                                    class="pa-3"
                                                                                    @click="addPallet(address.orderAddress)"
                                                                                >
                                                                                    <v-icon>
                                                                                        mdi-plus
                                                                                    </v-icon>
                                                                                </v-btn>
                                                                            </v-col>
                                                                        </v-card>
                                                                    </v-col>
                                                                </v-row>
                                                            </v-container>
                                                            <v-container v-else-if="!hasCollectingBox" class="pa-0 mt-5">
                                                                <v-row>
                                                                    <v-col cols="12" md="6">
                                                                        <v-card>
                                                                            <v-badge
                                                                                :value="hover"
                                                                                color="pink"
                                                                                content="Dane pobrane ze sklepu"
                                                                                overlap
                                                                                right
                                                                                transition="slide-x-transition"
                                                                            >
                                                                                <v-hover v-model="hover" :disabled="!address.orderAddress.isPmData">
                                                                                    <v-list>
                                                                                        <v-list-item>
                                                                                            <v-list-item-content>
                                                                                                <v-row no-gutters dense>
                                                                                                    <v-col cols="12" sm="7">
                                                                                                        <strong>Typ kartonu</strong>
                                                                                                    </v-col>
                                                                                                    <v-col cols="12" sm="5">
                                                                                                        <strong>Ilość</strong>
                                                                                                    </v-col>
                                                                                                </v-row>
                                                                                            </v-list-item-content>
                                                                                        </v-list-item>
                                                                                        <v-divider />
                                                                                        <v-list-item v-for="(box, boxIndex) in address.orderAddress.boxesInfo" :key="boxIndex">
                                                                                            <v-list-item-content>
                                                                                                <v-row no-gutters align="center" dense>
                                                                                                    <v-col cols="12" sm="7">
                                                                                                        {{ box.packageType }}
                                                                                                    </v-col>
                                                                                                    <v-col cols="12" sm="5">
                                                                                                        <v-text-field
                                                                                                            v-model="box.packageAmount"
                                                                                                            outlined
                                                                                                            readonly
                                                                                                            dense
                                                                                                            hide-details
                                                                                                            prepend-icon="mdi-minus"
                                                                                                            append-outer-icon="mdi-plus"
                                                                                                            @click:prepend="decreaseBoxAmount(address, boxIndex)"
                                                                                                            @click:append-outer="increaseBoxAmount(address, boxIndex)"
                                                                                                        />
                                                                                                    </v-col>
                                                                                                </v-row>
                                                                                            </v-list-item-content>
                                                                                        </v-list-item>
                                                                                        <v-divider />
                                                                                        <v-list-item>
                                                                                            <v-list-item-content>
                                                                                                <v-row no-gutters dense>
                                                                                                    <v-col cols="12" sm="7" class="text-right">
                                                                                                        <strong>Suma:</strong>
                                                                                                    </v-col>
                                                                                                    <v-col cols="12" offset-sm="" sm="5" class="text-center">
                                                                                                        {{ packageAmountSum(address) }}
                                                                                                    </v-col>
                                                                                                </v-row>
                                                                                            </v-list-item-content>
                                                                                        </v-list-item>
                                                                                    </v-list>
                                                                                </v-hover>
                                                                            </v-badge>
                                                                        </v-card>
                                                                    </v-col>
                                                                </v-row>
                                                            </v-container>
                                                            <v-container v-else class="pa-0 mt-5">
                                                                <v-row>
                                                                    <v-col cols="12">
                                                                        <v-card>
                                                                            <v-list>
                                                                                <v-list-item>
                                                                                    <v-list-item-content>
                                                                                        <v-row no-gutters dense>
                                                                                            <v-col cols="12" sm="3">
                                                                                                <strong>Typ kartonu</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3">
                                                                                                <strong>Ilość</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3">
                                                                                                <strong>Typ zbiorczego</strong>
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3">
                                                                                                <strong>Ilość</strong>
                                                                                            </v-col>
                                                                                        </v-row>
                                                                                    </v-list-item-content>
                                                                                </v-list-item>

                                                                                <v-divider />

                                                                                <v-list-item v-for="(box, boxIndex) in address.orderAddress.boxesInfo" :key="boxIndex">
                                                                                    <v-list-item-content>
                                                                                        <v-row no-gutters align="center" dense class="mb-2">
                                                                                            <v-col cols="12" sm="3">
                                                                                                {{ box.packageType }}
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3">
                                                                                                <v-text-field
                                                                                                    v-model="box.packageAmount"
                                                                                                    outlined
                                                                                                    class="mr-3"
                                                                                                    readonly
                                                                                                    dense
                                                                                                    hide-details
                                                                                                    prepend-icon="mdi-minus"
                                                                                                    append-outer-icon="mdi-plus"
                                                                                                    @click:prepend="decreaseBoxAmount(address, boxIndex)"
                                                                                                    @click:append-outer="increaseBoxAmount(address, boxIndex)"
                                                                                                />
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3">
                                                                                                {{ box.collectingBox.packageType }}
                                                                                            </v-col>
                                                                                            <v-col cols="12" sm="3">
                                                                                                {{ box.collectingBox.packageAmount }}
                                                                                            </v-col>
                                                                                        </v-row>
                                                                                        <v-divider />
                                                                                    </v-list-item-content>
                                                                                </v-list-item>
                                                                            </v-list>
                                                                        </v-card>
                                                                    </v-col>
                                                                </v-row>
                                                            </v-container>
                                                            <v-container class="pa-0 mt-5">
                                                                <v-row>
                                                                    <v-col cols="12">
                                                                        <v-alert
                                                                            v-for="(gift) in address.orderAddress.orderAddressGifts"
                                                                            :key="gift.id"
                                                                            dense
                                                                            outlined
                                                                            border="left"
                                                                            type="error"
                                                                        >
                                                                            {{ gift.userInfo }}
                                                                        </v-alert>
                                                                    </v-col>
                                                                </v-row>
                                                            </v-container>

                                                            <v-container v-if="!totalAmountUsed" class="pa-0">
                                                                <v-row justify="center">
                                                                    <v-alert
                                                                        type="error"
                                                                        dense
                                                                        outlined
                                                                    >
                                                                        Nakład nie jest prawidłowo rozdystrybuowany:
                                                                        <strong class="error--text">{{ totalAmountUsedValue }}</strong>
                                                                        /
                                                                        <strong class="error--text">{{ order.orderTotalAmount }}</strong>
                                                                    </v-alert>
                                                                </v-row>
                                                            </v-container>

                                                            <v-container class="pa-0">
                                                                <v-row justify="center">
                                                                    <v-btn
                                                                        v-if="hasUnitBoxChanged"
                                                                        class="mx-2"
                                                                        min-width="215"
                                                                        large
                                                                        depressed
                                                                        color="primary"
                                                                        data-sentry-label="Przelicz"
                                                                        :loading="actionLoading.recalculateUnitBox"
                                                                        @click="recalculateUnitBox(address)"
                                                                    >
                                                                        <v-icon left dark>
                                                                            mdi-calculator
                                                                        </v-icon>
                                                                        Przelicz
                                                                    </v-btn>
                                                                    <v-btn
                                                                        v-if="!hasUnitBoxChanged && !address.orderAddress.isSent"
                                                                        class="mx-2 mb-2"
                                                                        min-width="215"
                                                                        large
                                                                        depressed
                                                                        data-sentry-label="Wydrukuj etykiety"
                                                                        :color="isTemporaryLabelSelect ? 'info' : 'success'"
                                                                        :loading="actionLoading.printLabels"
                                                                        :outlined="isTemporaryLabelSelect"
                                                                        :disabled="(hasComments && areCommentsVisible) || (!isTemporaryLabelSelect && order.shippingBlocked) || order.isMultiProject ||
                                                                            isCashOnDeliveryPartialSent || isStandardCooperantOrder || !totalAmountUsed || isZeroPalletFields"
                                                                        @click="order.lastOrderFromPackage ? showConfirmSendPackageDialog(address) : printLabels(address)"
                                                                    >
                                                                        {{ isTemporaryLabelSelect ? 'Wydrukuj' : 'Nadaj' }}
                                                                    </v-btn>
                                                                    <v-btn
                                                                        v-if="!hasUnitBoxChanged && (address.orderAddress.isSent || address.orderAddress.isPartialShipping)"
                                                                        class="mx-2 mb-2"
                                                                        min-width="215"
                                                                        large
                                                                        color="primary"
                                                                        depressed
                                                                        outlined
                                                                        data-sentry-label="Wydrukuj ponownie"
                                                                        :loading="actionLoading.reprintLabels"
                                                                        :disabled="order.isMultiProject"
                                                                        @click="canApplyChangesWithoutPin ? showReprintLabelsDialog() : showPinDialog('reprintLabels')"
                                                                    >
                                                                        Wydrukuj ponownie
                                                                    </v-btn>
                                                                    <v-btn
                                                                        v-if="!hasUnitBoxChanged && address.orderAddress.isSent"
                                                                        class="mx-2 mb-2"
                                                                        min-width="215"
                                                                        large
                                                                        color="primary"
                                                                        depressed
                                                                        data-sentry-label="Generuj nowe"
                                                                        :disabled="actionLoading.reprintLabels || address.orderAddress.amountToShip === 0 || isCashOnDeliveryPartialSent
                                                                            || isStandardCooperantOrder || isZeroPalletFields"
                                                                        :loading="actionLoading.printLabels"
                                                                        @click="canApplyChangesWithoutPin ? printLabels(address) : showPinDialog('generateNewLabels')"
                                                                    >
                                                                        Generuj nowe
                                                                    </v-btn>
                                                                    <v-btn
                                                                        v-if="canPrintLieferschein"
                                                                        class="mx-2"
                                                                        min-width="215"
                                                                        large
                                                                        color="primary"
                                                                        depressed
                                                                        data-sentry-label="Wydrukuj Lieferschein"
                                                                        :loading="actionLoading.printLieferschein"
                                                                        @click="printLieferschein()"
                                                                    >
                                                                        Wydrukuj Lieferschein
                                                                    </v-btn>
                                                                </v-row>
                                                            </v-container>
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-carousel-item>
                                        </v-carousel>
                                    </v-card>
                                </v-col>

                                <v-col cols="12" lg="3">
                                    <v-card>
                                        <v-toolbar flat dense color="grey lighten-2">
                                            <v-toolbar-title>Parametry zamówienia</v-toolbar-title>
                                        </v-toolbar>
                                        <v-card-text>
                                            <v-container class="pa-0">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <table class="table table--simple">
                                                            <tbody>
                                                                <tr>
                                                                    <th>Status zamówienia:</th>
                                                                    <td>
                                                                        <template v-if="order.isSent">
                                                                            <strong class="success--text">nadane</strong>
                                                                        </template>
                                                                        <template v-else>
                                                                            <strong class="error--text">nie nadane</strong>
                                                                        </template>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Numer zamówienia:</th>
                                                                    <td><strong>{{ order.orderFullNumberCFPrint }}</strong></td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Rodzaj produktu:</th>
                                                                    <td>{{ order.orderProductType }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Nakład:</th>
                                                                    <td>{{ order.orderTotalAmount }} szt.</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Format:</th>
                                                                    <td>{{ order.orderProductFormat }}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>

                                                        <v-divider v-if="filePreviewURI" />

                                                        <v-img
                                                            v-if="filePreviewURI"
                                                            :src="filePreviewURI"
                                                            max-height="250"
                                                            contain
                                                            class="mt-4"
                                                            @click="showFilePreviewDialog"
                                                        >
                                                            <template #placeholder>
                                                                <v-row
                                                                    class="fill-height ma-0"
                                                                    align="center"
                                                                    justify="center"
                                                                >
                                                                    <v-progress-circular indeterminate color="primary" />
                                                                </v-row>
                                                            </template>
                                                        </v-img>
                                                    </v-col>
                                                </v-row>
                                            </v-container>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-fade-transition>
                    <v-fade-transition v-else>
                        <v-container v-if="isReportingSessionActive && !isPackageFound" fluid class="pa-0">
                            <v-row>
                                <v-col cols="12">
                                    <v-card>
                                        <v-card-text>
                                            <v-alert
                                                type="info"
                                                text
                                                border="left"
                                            >
                                                Nie odnaleziono paczki.
                                            </v-alert>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-container v-if="isReportingSessionActive && isPackageFound" fluid class="pa-0">
                            <v-row>
                                <v-col cols="12" lg="8">
                                    <v-btn
                                        v-if="hasParcelOrdersComments && !areParcelOrdersCommentsVisible"
                                        color="error"
                                        outlined
                                        x-large
                                        block
                                        class="mb-3"
                                        data-sentry-label="Pokaż uwagi do zamówienia"
                                        @click="areParcelOrdersCommentsVisible = true"
                                    >
                                        Pokaż uwagi do zamówienia
                                    </v-btn>

                                    <v-alert
                                        v-if="hasParcelOrdersComments"
                                        v-model="areParcelOrdersCommentsVisible"
                                        type="error"
                                        prominent
                                        dark
                                        elevation="2"
                                    >
                                        <span class="text-uppercase">Uwagi do zamówienia</span>

                                        <v-divider
                                            class="my-4"
                                            color="error"
                                        />

                                        <div v-for="(ordersComment, index) in parcel.ordersComments" :key="index">
                                            {{ index + 1 }}. {{ ordersComment.orderNumber }} - {{ ordersComment.comment }}
                                        </div>

                                        <v-divider
                                            class="my-4"
                                            color="error"
                                        />
                                        <v-btn
                                            depressed
                                            outlined
                                            large
                                            block
                                            class="mt-5"
                                            data-sentry-label="Potwierdzenie zapoznania się z komentarzem"
                                            @click="confirmParcelOrdersCommentsRead()"
                                        >
                                            Potwierdzam zapoznanie się z komentarzem
                                        </v-btn>
                                    </v-alert>
                                    <v-alert
                                        v-if="parcel.isStandardCooperantOrder"
                                        type="warning"
                                        icon="mdi-alert"
                                        border="left"
                                        class="alertText"
                                    >
                                        Ta paczka wykonywana jest w kooperacji. Nadanie na linii nie jest możliwe.
                                    </v-alert>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" lg="8">
                                    <v-card>
                                        <v-container class="px-0">
                                            <v-row>
                                                <v-col v-if="!parcel.isPallet" cols="10" offset="1">
                                                    <h2 class="text-h5 mb-4">
                                                        {{ parcel.deliveryAddress.company }} {{ parcel.deliveryAddress.firstName }} {{ parcel.deliveryAddress.lastName }}, {{ parcel.deliveryAddress.street }}, {{ parcel.deliveryAddress.postCode }} {{ parcel.deliveryAddress.city }}, {{ parcel.deliveryAddress.country }}
                                                    </h2>
                                                    <div class="d-inline-flex mr-3">
                                                        <p class="my-0">
                                                            Status:
                                                            <strong :class="parcel.isSent ? 'success--text' : 'error--text'">
                                                                {{ parcel.isSent ? 'Wysłana' : 'Nie wysłana' }}
                                                            </strong>
                                                        </p>
                                                    </div>

                                                    <div class="d-inline-flex ml-3 mr-2">
                                                        <p class="d-flex align-center my-0">
                                                            Ilość kartonów: <strong class="mx-1">{{ parcel.numberOfPackages }}</strong>
                                                            <v-btn
                                                                v-if="!parcel.isSent"
                                                                class="ml-1"
                                                                icon
                                                                color="primary"
                                                                small
                                                                data-sentry-label="Edytuj ilość kartonów"
                                                                @click="canApplyChangesWithoutPin ? showParcelPackagesAmountDialog() : showPinDialog('parcelPackagesAmount')"
                                                            >
                                                                <v-icon small>
                                                                    mdi-pencil
                                                                </v-icon>
                                                            </v-btn>
                                                        </p>
                                                    </div>

                                                    <div class="d-inline-flex mx-2">
                                                        <p class="d-flex align-center my-0">
                                                            Nakład: <strong class="mx-1">{{ parcel.amountToShip }} szt.</strong>
                                                        </p>
                                                    </div>

                                                    <div class="d-inline-flex ml-2 mr-3">
                                                        <p class="my-0">
                                                            Waga: <strong>{{ formatWeight(parcel.totalWeight) }}</strong>
                                                        </p>
                                                    </div>

                                                    <div class="d-inline-flex ml-3">
                                                        <p class="d-flex align-center">
                                                            Kurier: <strong class="mx-1">{{ parcel.carrier.shortCode }} ({{ parcel.carrier.deliveryTypeDescription }})</strong>
                                                            <v-btn
                                                                v-if="!parcel.isSent"
                                                                class="ml-1"
                                                                icon
                                                                color="primary"
                                                                small
                                                                data-sentry-label="Zmień kuriera"
                                                                @click="canApplyChangesWithoutPin ? showParcelCourierDialog() : showPinDialog('parcelCourier')"
                                                            >
                                                                <v-icon small>
                                                                    mdi-pencil
                                                                </v-icon>
                                                            </v-btn>
                                                        </p>
                                                    </div>

                                                    <v-btn
                                                        v-if="!parcel.isSent"
                                                        color="secondary"
                                                        class="mb-2"
                                                        depressed
                                                        data-sentry-label="Dodaj pakowanie"
                                                        :disabled="hasMaxNumberOfPackages"
                                                        @click="showPackageDialog(null)"
                                                    >
                                                        <v-icon left>
                                                            mdi-plus
                                                        </v-icon>
                                                        Dodaj pakowanie
                                                    </v-btn>
                                                    <v-container class="pa-0 mt-5">
                                                        <v-row>
                                                            <v-col cols="12">
                                                                <v-card>
                                                                    <v-data-table
                                                                        :items="boxesInfo"
                                                                        :headers="packagesHeaders"
                                                                        disable-pagination
                                                                        :mobile-breakpoint="0"
                                                                        hide-default-footer
                                                                        item-key="index"
                                                                    >
                                                                        <template #item.packageType="{item}">
                                                                            {{ item.packageType.name }}
                                                                        </template>
                                                                        <template #item.componentName="{item}">
                                                                            {{ item.component.name }}
                                                                        </template>
                                                                        <template #item.actions="{item}">
                                                                            <div class="d-inline-flex">
                                                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                                                    <template #activator="{on}">
                                                                                        <v-btn
                                                                                            icon
                                                                                            data-sentry-label="Edycja pakowania"
                                                                                            v-on="on"
                                                                                            @click="showPackageDialog(item)"
                                                                                        >
                                                                                            <v-icon color="teal">
                                                                                                mdi-pencil
                                                                                            </v-icon>
                                                                                        </v-btn>
                                                                                    </template>
                                                                                    <span>Edycja</span>
                                                                                </v-tooltip>
                                                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                                                    <template #activator="{on}">
                                                                                        <v-btn
                                                                                            icon
                                                                                            data-sentry-label="Potwierdzenie usunięcia pakowania"
                                                                                            v-on="on"
                                                                                            @click="showConfirmDeletePackageDialog(item)"
                                                                                        >
                                                                                            <v-icon color="pink">
                                                                                                mdi-delete
                                                                                            </v-icon>
                                                                                        </v-btn>
                                                                                    </template>
                                                                                    <span>Usuń</span>
                                                                                </v-tooltip>
                                                                            </div>
                                                                        </template>
                                                                    </v-data-table>
                                                                </v-card>
                                                            </v-col>
                                                        </v-row>
                                                    </v-container>
                                                    <v-container class="pa-0 mt-5">
                                                        <v-row justify="center">
                                                            <v-btn
                                                                v-if="!parcel.isSent"
                                                                class="mx-2"
                                                                min-width="215"
                                                                large
                                                                depressed
                                                                data-sentry-label="Wydrukuj etykiety dla paczki"
                                                                :color="isTemporaryLabelSelectInParcel ? 'info' : 'success'"
                                                                :loading="actionLoading.printPackageLabels"
                                                                :outlined="isTemporaryLabelSelectInParcel"
                                                                :disabled="(hasParcelOrdersComments && areParcelOrdersCommentsVisible) || (!isTemporaryLabelSelectInParcel && parcel.shippingBlocked) || parcel.isStandardCooperantType
                                                                    || !hasBoxesInfo"
                                                                @click="printPackageLabels()"
                                                            >
                                                                {{ isTemporaryLabelSelectInParcel ? 'Wydrukuj' : 'Nadaj' }}
                                                            </v-btn>
                                                            <v-btn
                                                                v-if="parcel.isSent"
                                                                class="mx-2"
                                                                min-width="215"
                                                                large
                                                                color="primary"
                                                                depressed
                                                                outlined
                                                                data-sentry-label="Wydrukuj ponownie"
                                                                :loading="actionLoading.reprintPackageLabels"
                                                                @click="canApplyChangesWithoutPin ? showReprintPackageLabelsDialog() : showPinDialog('reprintPackageLabels')"
                                                            >
                                                                Wydrukuj ponownie
                                                            </v-btn>
                                                        </v-row>
                                                    </v-container>
                                                </v-col>
                                                <v-col v-else cols="10" offset="1">
                                                    <v-alert
                                                        type="info"
                                                        text
                                                        border="left"
                                                    >
                                                        Przesyłka wysyłana paletą.
                                                    </v-alert>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-card>
                                </v-col>
                                <v-col cols="12" lg="4">
                                    <v-card>
                                        <v-toolbar flat dense color="grey lighten-2">
                                            <v-toolbar-title>Zamówienia</v-toolbar-title>
                                        </v-toolbar>
                                        <v-card-text>
                                            <v-container class="pa-0">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-data-table
                                                            :items="parcel.packageOrders"
                                                            :headers="ordersHeaders"
                                                            disable-pagination
                                                            :mobile-breakpoint="0"
                                                            hide-default-footer
                                                        >
                                                            <template #item.orderProductFormat="{item}">
                                                                <template v-if="item.orderProductFormat">
                                                                    {{ item.orderProductFormat }}
                                                                </template>
                                                                <template v-else>
                                                                    -
                                                                </template>
                                                            </template>
                                                            <template #item.actions="{item}">
                                                                <div class="d-inline-flex">
                                                                    <v-tooltip :open-delay="tooltipDelay" top>
                                                                        <template #activator="{on}">
                                                                            <v-btn
                                                                                icon
                                                                                data-sentry-label="Pobierz"
                                                                                :disabled="!item.fileUrl"
                                                                                v-on="on"
                                                                                @click="openInNewTab(item.fileUrl)"
                                                                            >
                                                                                <v-icon color="primary">
                                                                                    mdi-download
                                                                                </v-icon>
                                                                            </v-btn>
                                                                        </template>
                                                                        <span>Pobierz</span>
                                                                    </v-tooltip>
                                                                </div>
                                                            </template>
                                                        </v-data-table>
                                                    </v-col>
                                                </v-row>
                                            </v-container>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-fade-transition>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/store'

import AmountToShipDialog from './AmountToShipDialog'
import CodeReaderDialog from '@/components/common/CodeReaderDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import CourierDialog from './CourierDialog'
import CouriersListDialog from './CouriersListDialog'
import CreateAccidentDialog from '@/views/Maintenance/Accidents/CreateAccidentDialog'
import CreateShipmentProcessReportDialog from './CreateShipmentProcessReportDialog'
import ImagePreviewDialog from './ImagePreviewDialog'
import PackageAmountDialog from './PackageAmountDialog'
import PackageDialog from './PackageDialog'
import ParcelCourierDialog from './ParcelCourierDialog'
import ParcelPackagesAmountDialog from './ParcelPackagesAmountDialog'
import PinConfirmationDialog from './PinConfirmationDialog'
import ShipmentReportShiftSummaryDialog from '@/components/common/ShipmentReportShiftSummaryDialog'
import ReprintLabelsDialog from './ReprintLabelsDialog'
import ReprintPackageLabelsDialog from './ReprintPackageLabelsDialog'
import WorkspaceSettingsDialog from './WorkspaceSettingsDialog'

import dateFormatter from '@/mixins/dateFormatter'
import decodeQRcode from '@/mixins/decodeQRcode'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import { appConfig, apiConfig } from '@/config'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'Sendings',
    components: {
        AmountToShipDialog,
        CodeReaderDialog,
        ConfirmDialog,
        CourierDialog,
        CouriersListDialog,
        CreateAccidentDialog,
        CreateShipmentProcessReportDialog,
        ImagePreviewDialog,
        PackageAmountDialog,
        PackageDialog,
        ParcelCourierDialog,
        ParcelPackagesAmountDialog,
        PinConfirmationDialog,
        ShipmentReportShiftSummaryDialog,
        ReprintLabelsDialog,
        ReprintPackageLabelsDialog,
        WorkspaceSettingsDialog,
    },
    mixins: [
        dateFormatter,
        decodeQRcode,
        tooltipDelay,
        weightFormatter
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('shipments/sendings/getGates'),
            store.dispatch('shipments/sendings/getCouriers'),
            store.dispatch('configurations/getProductionSites', {query:'?type_id=1'}),
            store.dispatch('shipments/sendings/getUnfinishedShipmentsReportsShiftSummary', store.state.user.userId)
        ])
            .then(responses => {
                const workspace = JSON.parse(localStorage.getItem('sendingsWorkspace'))
                const unfinishedReportsShiftSummary = responses[3].data.results

                if (unfinishedReportsShiftSummary.length > 0) {

                    store.dispatch('reports/getDetailedShipmentsReports', {
                        shipmentReportShiftSummary_id: `${unfinishedReportsShiftSummary[0].id}`,
                        query: '?status_id=1'
                    })
                        .then(response => {
                            const unfinishedDetailedShipmentsReport = response.data.results

                            if (unfinishedDetailedShipmentsReport.length > 0 && unfinishedDetailedShipmentsReport[0].productionSiteId === workspace?.workspaceId) {
                                next({
                                    name: 'shipmentsSendingsReport',
                                    params: {
                                        id: unfinishedDetailedShipmentsReport[0].id
                                    }

                                })

                                store.dispatch('toggleMainLoader', false)
                            } else {
                                next(vm => {
                                    vm.couriers = responses[1].data.results

                                    if (!workspace) {
                                        vm.showWorkspaceSettingsDialog()
                                    } else {
                                        vm.workspaceId = workspace.workspaceId
                                        vm.setSendingsSettings(vm.workspaceId)
                                    }

                                    vm.unfinishedReportsShiftSummary = unfinishedReportsShiftSummary[0]
                                })
                            }
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                } else {
                    next(vm => {
                        if (!workspace) {
                            vm.showWorkspaceSettingsDialog()
                        } else {
                            vm.workspaceId = workspace.workspaceId
                            vm.setSendingsSettings(vm.workspaceId)
                        }
                    })
                }
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            unfinishedReportsShiftSummary: null,
            settings: {
                workspace: null
            },
            workspaceId: null,
            workspaceSettingsDialog: false,
            workspaceSettingsDialogPersistent: true,
            confirmStopReportingSessionDialog: false,
            hover: false, // TODO: Zmienić nazwę na bardziej zrozumiałą
            orderNumber: null,
            reportProcesses: [],
            createShipmentProcessReportDialog: false,
            order: {
                orderFullNumberCFPrint: null,
                orderProductType: null,
                orderTotalAmount: null,
                orderProductFormat: null,
                orderComments: [],
                packageId: null,
                addresses: [],
                isSent: false,
                cashOnDeliveryPartialSent: false,
                isStandardCooperantOrder: false
            },
            currentAddress: {
                orderAddress: {
                    carrier: {},
                    packageAmount: null
                }
            },
            couriersListDialog: false,
            couriers: [],
            hasUserHigherPermissions: false,
            addressCarouselIndex: 0,
            pin: appConfig.sendings.permissionsPin,
            pinConfirmationAction: null,
            pinConfirmationDialog: false,
            packageAmountDialog: false,
            amountToShipDialog: false,
            courierDialog: false,
            imagePreviewDialog: false,
            areCommentsVisible: true,
            hasUnitBoxChanged: false,
            unitBoxesToRecalculate: [],
            unitBox: {
                packageTypeId: null,
                packageAmount: null,
                totalProductsBoxType: null,
                productWeight: null
            },
            reprintLabelsDialog: false,
            actionLoading: {
                getOrder: false,
                printLabels: false,
                reprintLabels: false,
                recalculateUnitBox: false,
                createAccident: false,
                reportingSession: false,
                getPackage: false,
                updatePackage: false,
                approveReport: false,
                printPackageLabels: false,
                reprintPackageLabels: false,
                createShipmentProcessReport: false,
                printLieferschein: false
            },
            codeReaderDialog: false,
            codeReaderSettings: {
                expectedQrTypeCodes: null,
                origin: null
            },
            createAccidentDialog: false,
            accident: {
                id: null,
                machineId: null,
                accidentTypeId: null,
                description: null,
                accidentDepartmentId: null,
                username: null
            },
            currentDate: null,
            showConfirmSendPackage: false,
            showPackageComponent: false,
            parcelCourierDialog: false,
            parcel: {
                number: null,
                deliveryAddress: {},
                shipments: [],
                ordersComments: []
            },
            boxesInfo: [],
            packagesPalettList: [],
            parcelPackagesAmountDialog: false,
            currentPackage: null,
            currentPallet: null,
            boxTypes: [],
            packageTypes: [],
            packageDialog: false,
            confirmDeletePackageDialog: false,
            confirmDeletePalletDialog: false,
            reprintPackageLabelsDialog: false,
            shipmentReportShiftSummaryDialog: false,
            areParcelOrdersCommentsVisible: true,
            stoppedReportingSession: false,
            currentReportShiftSummary: null,
            packagesHeaders: [
                {
                    text: 'Typ kartonu',
                    value: 'packageType',
                    sortable: false
                },
                {
                    text: 'Ilość',
                    value: 'packageAmount',
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100,
                }
            ],
            ordersHeaders: [
                {
                    text: 'Numer zamówienia',
                    value: 'orderFullNumberCFPrint',
                    sortable: false
                },
                {
                    text: 'Format produktu',
                    value: 'orderProductFormat',
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Nakład',
                    value: 'orderTotalAmount',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100,
                }
            ],
            totalAmountUsedValue: 0,
            isZeroPalletFields: false,
        }
    },
    computed: {
        isOrderFound() {
            return this.order.orderFullNumberCFPrint !== null
        },
        isPackageFound() {
            return this.parcel.number !== null
        },
        hasComments() {
            return this.order.orderComments ? this.order.orderComments.length > 0 : false
        },
        hasParcelOrdersComments() {
            return this.parcel.ordersComments ? this.parcel.ordersComments.length > 0 : false
        },
        hasBoxesInfo() {
            return this.boxesInfo ? this.boxesInfo.length > 0 : false
        },
        hasCollectingBox() {
            return this.currentAddress?.orderAddress?.boxesInfo?.some(item => item.collectingBox !== null)
        },
        isTemporaryLabelSelect() {
            return this.currentAddress?.orderAddress?.carrier?.id === 6
        },
        isTemporaryLabelSelectInParcel() {
            return this.parcel?.carrier?.id === 6
        },
        filePreviewURI() {
            return this.order.fileUrl ? `${apiConfig.FACTORYBASE_API_URI}${this.order.fileUrl}` : null
        },
        hasCamera() {
            return this.$store.state.common.hasCamera
        },
        canApplyChangesWithoutPin() {
            return hasUserPermission('sendings_without_pin') || this.hasUserHigherPermissions
        },
        userName() {
            return this.$store.state.user.userName
        },
        sendingsSettings() {
            return this.$store.state.shipments.sendings.sendings.sendingsSettings
        },
        currentWorkspace() {
            return this.sendingsSettings.workspace
        },
        workspaces() {
            return this.$store.state.configurations.productionSites.productionSites
        },
        canReportOnWorkspace() {
            return this.workspaces.find(item => item.id === this.workspaceId)
        },
        canChangeWorkspace() {
            return hasUserPermission('production_sites_admin_update')
        },
        isReportingSessionActive() {
            return this.unfinishedReportsShiftSummary !== null
        },
        isCashOnDeliveryPartialSent() {
            return this.order.cashOnDeliveryPartialSent
        },
        isStandardCooperantOrder() {
            return this.order.isStandardCooperantOrder
        },
        hasMaxNumberOfPackages() {
            return this.boxesInfo?.reduce((acc, box) => acc + box.packageAmount, 0) === this.parcel.numberOfPackages
        },
        totalAmountUsed() {
            if (this.currentAddress.orderAddress.deliveryType !== 'PALET') {
                return true
            }

            return +this.order.orderTotalAmount === this.totalAmountUsedValue
        },
        currentMachine() {
            return this.sendingsSettings.workspace.machine
        },
        isWMDOrder() {
            return this.order.orderFullNumberCFPrint && this.order.orderFullNumberCFPrint.startsWith('WMD')
        },
        hasLieferscheinPrinter() {
            const sendingsWorkspace = JSON.parse(localStorage.getItem('sendingsWorkspace'))

            return sendingsWorkspace && sendingsWorkspace.lieferscheinPrinter
        },
        canPrintLieferschein() {
            return this.isWMDOrder && this.hasLieferscheinPrinter
        }
    },
    watch: {
        addressCarouselIndex: {
            handler: function(index) {
                this.currentAddress = this.order.addresses[index]
            }
        }
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
        this.updateCurrentDate()
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        calculateTotalAmountUsed() {
            this.totalAmountUsedValue = this.currentAddress.orderAddress.boxesInfo.reduce(
                (total, item) => total + +item.singleBoxProductsAmount, 0
            )
            this.isZeroPalletFields = this.currentAddress.orderAddress.deliveryType === 'PALET' &&
                0 !== this.currentAddress.orderAddress.boxesInfo.filter(
                    box => +box.singleBoxProductsAmount === 0 || +box.packageAmount === 0
                ).length
        },
        updateCurrentDate() {
            this.currentDate = this.formatDate(new Date(), true, true, { weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric' })
        },
        formatAddressHeader(address) {
            if (address.orderNumber) {
                return address.orderNumber
            }

            const recipient = [
                address.company,
                address.firstName,
                address.lastName
            ].filter(Boolean).join(' ')

            const addressDetails = [
                address.street,
                [address.postCode, address.city].filter(Boolean).join(' '),
                address.country
            ].filter(Boolean).join(', ')

            return [recipient, addressDetails].filter(Boolean).join(', ')
        },
        async getpackagesPalettList() {
            const query = '?boxTypeId=3'

            await this.$store.dispatch('shipments/shipmentsOrders/getPackagesTypes', query)
                .then(responses => {
                    this.packagesPalettList = responses.data.results
                })
                .catch(error => errorHandler(error))
        },
        addPallet(orderAddress) {
            let newPalet = Object.assign({}, orderAddress.boxesInfo[orderAddress.boxesInfo.length - 1])
            
            newPalet.singleBoxProductsAmount = null
            newPalet.packageAmount = null
            newPalet.totalWeight = 0
            orderAddress.boxesInfo.push(newPalet)
            this.calculateTotalAmountUsed()
        },  
        async getWorkspace(workspaceId) {
            return await this.$store.dispatch('configurations/getProductionSite', workspaceId)
                .catch(error => {
                    errorHandler(error)
                })
        },
        setSendingsSettings(workspaceId) {
            this.getWorkspace(workspaceId).then(response => {
                this.settings.workspace = response.data

                const workspaceData = {
                    'workspaceId': workspaceId,
                    'lieferscheinPrinter': response.data.lieferscheinPrinter ? true : false
                }

                localStorage.setItem('sendingsWorkspace', JSON.stringify(workspaceData))

                store.dispatch('shipments/sendings/setSendingsSettings', this.settings)
            })
        },
        showWorkspaceSettingsDialog() {
            addSentryBreadcrumb('ui.action', 'Ustawienia stanowiska')

            this.$store.dispatch('configurations/getProductionSitesBase', {query:'?type_id=1'})
                .then(() => {
                    this.workspaceSettingsDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.reportingSession = false
                })
        },
        closeWorkspaceSettingsDialog() {
            this.workspaceSettingsDialog = false
        },
        saveWorkspaceSettings(value) {
            this.workspaceId = parseInt(value)
            this.getWorkspace(this.workspaceId).then(response => {
                const workspaceData = {
                    'workspaceId': value,
                    'lieferscheinPrinter': response.data.lieferscheinPrinter
                }

                localStorage.setItem('sendingsWorkspace', JSON.stringify(workspaceData))
                this.setSendingsSettings(this.workspaceId)
                this.closeWorkspaceSettingsDialog()
            })
        },
        startReportingSession() {
            if (!this.workspaceId) {
                if (!this.canChangeWorkspace) this.actionLoading.reportingSession = true
                this.showWorkspaceSettingsDialog()
            }
            else if (!this.canReportOnWorkspace && !this.canChangeWorkspace) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można rozpocząć raportowania na wybranym stanowisku.'
                })
            } else {
                this.actionLoading.reportingSession = true

                this.$store.dispatch('shipments/sendings/startShipmentReportingSession')
                    .then(response => {
                        this.unfinishedReportsShiftSummary = response.data

                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Rozpoczęto sesję.'
                        })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading.reportingSession = false
                    })
            }
        },
        showConfirmStopReportingSessionDialog() {
            this.confirmStopReportingSessionDialog = true
        },
        closeConfirmStopReportingSessionDialog() {
            this.confirmStopReportingSessionDialog = false
            setTimeout(() => {
                this.order = {}
                this.order.orderFullNumberCFPrint = null
                this.order.orderProductType = null,
                this.order.orderTotalAmount = null,
                this.order.orderProductFormat = null,
                this.order.orderComments = [],
                this.order.packageId = null,
                this.order.addresses = [],
                this.orderNumber = null,
                this.order.isSent = false
            }, 500)
        },
        stopReportingSession() {
            this.actionLoading.reportingSession = true
            this.$store.dispatch('shipments/sendings/stopShipmentReportingSession', {
                id: this.unfinishedReportsShiftSummary.id,
                statusId: 10
            })
                .then((response) => {
                    this.unfinishedReportsShiftSummary = response.data
                    this.showShipmentReportShiftSummaryDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmStopReportingSessionDialog()
                    this.actionLoading.reportingSession = false
                })
        },
        
        showShipmentReportShiftSummaryDialog() {
            this.stoppedReportingSession = true
            this.$store.dispatch('toggleMainLoader', true)

            this.$store.dispatch('reports/getDetailedShipmentsReports', {
                shipmentReportShiftSummary_id : this.unfinishedReportsShiftSummary.id
            })
                .then(response => {
                    this.currentReportShiftSummary = {
                        ...this.unfinishedReportsShiftSummary,
                        detailedReports: response.data.results
                    }

                    this.shipmentReportShiftSummaryDialog = true
                    this.unfinishedReportsShiftSummary = null
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$store.dispatch('toggleMainLoader', false)
                })
        },
        closeShipmentReportShiftSummaryDialog() {
            this.currentReportShiftSummary = null
            this.shipmentReportShiftSummaryDialog = false
            setTimeout(() => {
                this.stoppedReportingSession = false
            }, 500)
        },
        orderAddressSendStatusClass(addressIndex) {
            const statusId = this.order.addresses[addressIndex].orderAddress.orderAddressStatus.id

            switch (statusId) {
                case 10:
                    return 'error--text'
                case 50:
                    return 'warning--text'
                case 100:
                    return 'success--text'
                default:
                    break
            }

            return 'success--text'
        },
        async getOrder(
            loader = true,
            resetCarousel = true,
            showComments = true,
            carrierId = null
        ) {
            this.closePinConfirmationDialog()
            this.closeCourierDialog()
            this.boxesInfo = []
            this.actionLoading.getOrder = loader

            let query = `?orderNumber=${this.orderNumber}`

            if (carrierId) {
                query = `${query}&carrierId=${carrierId}`
            }

            return await this.$store.dispatch('shipments/sendings/getOrder', query )
                .then(response => {
                    if (response.data.isMultiProject) {
                        this.$store.dispatch('showNotification', {
                            type: 'info',
                            message: 'Zamówienie wielowzorowe. Nie można nadać na linii.'
                        })
                    }
                    this.showPackageComponent = false
                    this.order = response.data
                    this.order.addresses.forEach(address => { address.orderAddress.packageAmountSum = this.packageAmountSum(address) })

                    this.order.addresses.forEach(address => { 
                        address.orderAddress.deliveryType === 'PALET' && !this.packagesPalettList.length && this.getpackagesPalettList()
                    })

                    if (resetCarousel) {
                        setTimeout(() => {
                            this.addressCarouselIndex = 0
                        }, 1) // Carousel FIX
                        this.currentAddress = this.order.addresses[0]
                    } else {
                        this.currentAddress = this.order.addresses[this.addressCarouselIndex]
                    }

                    if (showComments) {
                        this.areCommentsVisible = this.order.addresses.some(address => !address.isSent)
                    } else {
                        this.areCommentsVisible = false
                    }

                    this.order.addresses.forEach(address => {
                        address.orderAddress.boxesInfo.forEach((box) => {
                            box.id = box.packageTypeId
                        })
                    })

                    this.calculateTotalAmountUsed()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.getOrder = false
                    this.hasUnitBoxChanged = false
                })
        },
        showCouriersListDialog() {
            this.couriersListDialog = true
            addSentryBreadcrumb('ui.action', 'Podgląd listy przewoźników')
        },
        closeCouriersListDialog() {
            this.couriersListDialog = false
        },
        confirmCommentsRead() {
            this.areCommentsVisible = false
        },
        confirmParcelOrdersCommentsRead() {
            this.areParcelOrdersCommentsVisible = false
        },
        showPinDialog(actionName) {
            this.pinConfirmationAction = actionName
            this.pinConfirmationDialog = true
        },
        closePinConfirmationDialog() {
            this.pinConfirmationAction = null
            this.pinConfirmationDialog = false
        },
        confirmPin() {
            this.pinConfirmationDialog = false

            switch (this.pinConfirmationAction) {
                case 'packageAmount':
                    this.showPackageAmountDialog(this.currentAddress)
                    break
                case 'courier':
                    this.showCourierDialog(this.currentAddress)
                    break
                case 'reprintLabels':
                    this.showReprintLabelsDialog()
                    break
                case 'generateNewLabels':
                    this.printLabels(this.currentAddress)
                    break
                case 'parcelPackagesAmount':
                    this.showParcelPackagesAmountDialog()
                    break
                case 'parcelCourier':
                    this.showParcelCourierDialog()
                    break
                case 'reprintPackageLabels':
                    this.showReprintPackageLabelsDialog()
                    break
            }
        },
        showParcelPackagesAmountDialog() {
            this.closePinConfirmationDialog()

            setTimeout(() => {
                this.parcelPackagesAmountDialog = true
            }, 500)
        },
        closeParcelPackagesAmountDialog() {
            this.hasUserHigherPermissions = false
            this.parcelPackagesAmountDialog = false
        },
        updateParcel(payload) {
            const deliveryTypeDescription = payload.parcel.carrier.deliveryTypeDescription

            payload.parcel.carrier = payload.carrier ? {...payload.carrier, deliveryTypeDescription: deliveryTypeDescription} :
                {...payload.parcel.carrier, deliveryTypeDescription: deliveryTypeDescription}

            this.parcel = payload.parcel
            this.closeParcelPackagesAmountDialog()
        },
        showPackageAmountDialog() {
            this.closePinConfirmationDialog()

            setTimeout(() => {
                this.packageAmountDialog = true
            }, 500)
        },
        closePackageAmountDialog() {
            this.hasUserHigherPermissions = false
            this.packageAmountDialog = false
        },
        showAmountToShipDialog() {
            this.amountToShipDialog = true
        },
        closeAmountToShipDialog() {
            this.amountToShipDialog = false
        },
        showCourierDialog() {
            this.closePinConfirmationDialog()

            setTimeout(() => {
                this.courierDialog = true
            }, 500)
        },
        closeCourierDialog() {
            this.hasUserHigherPermissions = false
            this.courierDialog = false
        },
        showParcelCourierDialog() {
            this.closePinConfirmationDialog()

            setTimeout(() => {
                this.parcelCourierDialog = true
            }, 500)
        },
        closeParcelCourierDialog() {
            this.hasUserHigherPermissions = false
            this.parcelCourierDialog = false
        },
        updateSendingParameters(address) {
            const index = this.order.addresses.findIndex(item => item.id === address.id)

            const deliveryTypeDescription = this.order.addresses[index].orderAddress.carrier.deliveryTypeDescription

            address.orderAddress.carrier = {...address.orderAddress.carrier, deliveryTypeDescription: deliveryTypeDescription}

            this.currentAddress = address
            this.order.addresses.splice(index, 1, address)
        },
        decreaseBoxAmount(address, boxIndex) {
            if (this.packageAmountSum(address) > 1) {
                const box = address.orderAddress.boxesInfo[boxIndex]

                box.packageAmount > 0 ? box.packageAmount-- : false

                if (box.collectingBox !== null) {
                    this.changeUnitBox(address, box)
                }
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Osiągnięto minimalną ilość kartonów.'
                })
            }
        },
        increaseBoxAmount(address, boxIndex) {
            if (this.packageAmountSum(address) < address.orderAddress.packageAmountSum) {
                const box = address.orderAddress.boxesInfo[boxIndex]

                box.packageAmount++

                if (box.collectingBox !== null) {
                    this.changeUnitBox(address, box)
                }
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Osiągnięto maksymalną ilość kartonów.'
                })
            }
        },
        changeUnitBox(address, box) {
            this.hasUnitBoxChanged = true
            this.unitBox.id = box.id
            this.unitBox.packageTypeId = box.packageTypeId
            this.unitBox.packageAmount = box.packageAmount
            this.unitBox.totalProductsBoxType = box.totalProductsBoxType
            this.unitBox.productWeight = address.orderAddress.productWeight
            this.unitBox.addressId = address.id

            const unitBoxIndex = this.unitBoxesToRecalculate.findIndex(item => item.id === box.id),
                unitBox = JSON.parse(JSON.stringify(this.unitBox))

            unitBoxIndex > -1 ? this.unitBoxesToRecalculate.splice(unitBoxIndex, 1, unitBox) : this.unitBoxesToRecalculate.push(unitBox)
        },
        packageAmountSum(address) {
            const boxes = address.orderAddress.boxesInfo
            const isPallet = address.orderAddress.isPallet
            let sum = 0

            boxes.forEach(box => sum += isPallet ? 1 : box.packageAmount)

            return sum
        },
        showFilePreviewDialog() {
            this.imagePreviewDialog = true
        },
        closeImagePreviewDialog() {
            this.imagePreviewDialog = false
        },
        recalculateUnitBox(address) {
            this.actionLoading.recalculateUnitBox = true
            let promises = []

            this.unitBoxesToRecalculate.forEach(unitBox => {
                promises.push(this.$store.dispatch('shipments/sendings/recalculateUnitBox', unitBox))
            })

            Promise.all(promises)
                .then(responses => {
                    responses.forEach(response => {
                        const addressIndex = this.order.addresses.findIndex(address => response.data.addressId === address.id),
                            unitBoxIndex = this.order.addresses[addressIndex].orderAddress.boxesInfo.findIndex(box => response.data.packageTypeId == box.packageTypeId),
                            unitBox = this.order.addresses[addressIndex].orderAddress.boxesInfo[unitBoxIndex]

                        unitBox.packageTypeId = response.data.packageTypeId
                        unitBox.packageAmount = response.data.packageAmount
                        unitBox.totalProductsBoxType = response.data.totalProductsBoxType
                        unitBox.collectingBox = response.data.collectingBox
                        unitBox.totalWeight = response.data.totalWeight
                    })
                    this.resetRecalculateUnitBox()
                    address.orderAddress.packageAmountSum = this.packageAmountSum(address)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.recalculateUnitBox = false
                })
        },
        resetRecalculateUnitBox() {
            this.unitBox.packageTypeId = null
            this.unitBox.packageAmount = null
            this.unitBox.totalProductsBoxType = null
            this.unitBox.productWeight = null
            this.hasUnitBoxChanged = false
            this.unitBoxesToRecalculate = []
        },
        async updateOrderAddress(address) {
            return await this.$store.dispatch('shipments/sendings/updateOrderAddress', address.orderAddress)
                .catch(error => {
                    throw error
                })
        },
        showConfirmSendPackageDialog(address) {
            this.currentAddress = address
            this.showConfirmSendPackage = true
        },
        confirmSendPackage() {
            this.showPackageOrdersComponent()
            this.order.lastOrderFromPackage ? this.showPackageOrdersComponent() : this.printLabels(this.currentAddress)
        },
        closeConfirmSendPackageDialog() {
            this.showConfirmSendPackage = false
            this.printLabels(this.currentAddress)
        },
        showPackageOrdersComponent() {
            this.actionLoading.getPackage = true
            this.$store.dispatch('shipments/sendings/getPackage', this.order.packageNumber)
                .then(response => {
                    this.parcel = response.data
                    this.showPackageComponent = true
                    this.showConfirmSendPackage = false
                    this.areCommentsVisible = this.parcel.isSent
                    this.parcel.ordersComments = []
                    this.parcel.packageOrders.forEach(order => order.orderComments.forEach(comment => this.parcel.ordersComments.push({
                        orderNumber : order.orderFullNumberCFPrint, comment : comment
                    })))
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.getPackage = false
                })
        },
        hidePackageOrdersComponent() {
            this.showPackageComponent = false
        },
        printLabels(address) {
            if (!this.workspaceId) {
                this.showWorkspaceSettingsDialog()
            } else {
                this.actionLoading.printLabels = true
                address.orderAddress.boxesInfo = address.orderAddress.boxesInfo.map((box) => {
                    box.packageAmount = parseInt(box.packageAmount) 
                    box.singleBoxProductsAmount = parseInt(box.singleBoxProductsAmount) 
                    
                    return box
                })

                this.updateOrderAddress(address)
                    .then(() => {
                        this.$store.dispatch('shipments/sendings/printSendingsLabels', {
                            productionSiteId: this.workspaceId,
                            startOfPacking: this.order.startOfPacking,
                            deliveries: [{
                                addressId: address.orderAddress.id,
                                amountToShip: address.orderAddress.amountToShip,
                                carrierId: address.orderAddress.carrier.id,
                                pallet: address.orderAddress.isPallet,
                                boxesInfo: address.orderAddress.boxesInfo
                            }]
                        })
                            .then(response => {
                                const carrierId = response.data.carrierId

                                this.getOrder(false, false, false, carrierId)
                                    .catch(error => {
                                        errorHandler(error)
                                    })
                                    .finally(() => {
                                        this.actionLoading.printLabels = false
                                    })

                                this.$store.dispatch('showNotification', {
                                    type: 'success',
                                    message: 'Etykieta została wydrukowana.'
                                })
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.actionLoading.printLabels = false
                            })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            }

        },
        printLieferschein() {
            if (!this.workspaceId) {
                this.showWorkspaceSettingsDialog()
            } else {
                this.actionLoading.printLieferschein = true

                this.$store.dispatch('reports/printLieferschein', {
                    productionSiteId: this.workspaceId,
                    orderNumber: this.orderNumber
                })
                    .then(() => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Lieferschein został wydrukowany.'
                        })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading.printLieferschein = false
                    })
            }
        },
        printPackageLabels() {
            if (!this.workspaceId) {
                this.showWorkspaceSettingsDialog()
            } else {
                this.actionLoading.printPackageLabels = true
                this.$store.dispatch('shipments/sendings/printPackageLabels', {
                    productionSiteId: this.workspaceId,
                    startOfPacking: this.parcel.startOfPacking,
                    packageNumber: this.parcel.packageNumber,
                    addressId: this.parcel.orderAddressId,
                    amountToShip: this.parcel.amountToShip,
                    carrierId: this.parcel.carrier.id,
                    boxesInfo: this.boxesInfo
                })
                    .then(response => {
                        this.$store.dispatch('shipments/sendings/getPackage', response.data.packageNumber)
                            .then(response => {
                                this.parcel = response.data
                                this.showPackageComponent = true
                                this.showConfirmSendPackage = false
                                this.areCommentsVisible = this.parcel.isSent
                                this.parcel.ordersComments = []
                                this.parcel.packageOrders.forEach(order => order.orderComments.forEach(comment => this.parcel.ordersComments.push({
                                    orderNumber : order.orderFullNumberCFPrint, comment : comment
                                })))
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.actionLoading.printPackageLabels = false
                            })
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Etykieta została wydrukowana.'
                        })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading.printPackageLabels = false
                    })
            }
        },
        showReprintLabelsDialog() {
            this.reprintLabelsDialog = true
        },
        closeReprintLabelsDialog() {
            this.hasUserHigherPermissions = false
            this.reprintLabelsDialog = false
        },
        reprintLabels(shipmentIds) {
            if (!this.workspaceId) {
                this.showWorkspaceSettingsDialog()
            } else {
                this.actionLoading.reprintLabels = true

                this.$store.dispatch('shipments/sendings/reprintSendingsLabels', {
                    productionSiteId: this.workspaceId,
                    shipments: shipmentIds
                })
                    .catch(error => errorHandler(error))
                    .finally(() => {
                        this.actionLoading.reprintLabels = false
                    })
            }
        },
        showReprintPackageLabelsDialog() {
            this.reprintPackageLabelsDialog = true
        },
        closeReprintPackageLabelsDialog() {
            this.hasUserHigherPermissions = false
            this.reprintPackageLabelsDialog = false
        },
        reprintPackageLabels(shipmentIds) {
            if (!this.workspaceId) {
                this.showWorkspaceSettingsDialog()
            } else {
                this.actionLoading.reprintPackageLabels = true

                this.$store.dispatch('shipments/sendings/reprintPackageLabels', {
                    productionSiteId: this.workspaceId,
                    shipments: shipmentIds
                })
                    .catch(error => errorHandler(error))
                    .finally(() => {
                        this.actionLoading.reprintPackageLabels = false
                    })
            }
        },
        showCodeReaderDialog(data) {
            this.codeReaderSettings = data
            this.codeReaderDialog = true
        },
        hideCodeReaderDialog() {
            this.codeReaderDialog = false
        },
        handleQrJSON({ data }) {
            if (typeof data.taskTypeId !== 'undefined' && data.type === 'KP') {
                this.orderNumber = data.order
                this.getOrder()
            } else if (this.isOrderFound && data.type === 'ROLE' && data.roleName === 'sendings' && data.pin === this.pin) {
                this.hasUserHigherPermissions = true

                this.$store.dispatch('showNotification', {
                    type: 'info',
                    message: 'Odblokowano wyższe uprawnienia użytkownika.'
                })

                if (this.pinConfirmationDialog) {
                    this.confirmPin()
                }
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('reports.messages.unsupportedQrCode')
                })
            }
        },
        showCreateAccidentDialog() {
            addSentryBreadcrumb('ui.action', 'Zgłoszenie awarii')
            this.accident.machineId = this.currentMachine.id
            this.actionLoading.createAccident = true

            Promise.all([
                this.$store.dispatch('maintenance/getAccidentTypes'),
                this.$store.dispatch('maintenance/getMachines'),
                this.$store.dispatch('maintenance/getAccidentDepartments'),
                this.$store.dispatch('maintenance/getSoftwareTypes')
            ])
                .then(() => {
                    this.createAccidentDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createAccident = false
                })

        },
        closeCreateAccidentDialog() {
            this.createAccidentDialog = false

            setTimeout(() => {
                this.accident.id = null
                this.accident.accidentTypeId = null,
                this.accident.machineId = null,
                this.accident.description = null,
                this.accident.username = null,
                this.accident.accidentDepartmentId = null
            }, 500)
        },
        createAccident() {
            this.accident.username = this.userName
            this.$store.dispatch('maintenance/createAccident', this.accident)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeCreateAccidentDialog()
                })
        },
        openInNewTab(fileUrl) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            window.open(`${mediaRoot}${fileUrl}`, '_blank').focus()
        },
        indexedParcelBoxesInfo() {
            this.boxesInfo.forEach(item => {
                delete item.index
            })
            this.boxesInfo = this.boxesInfo.map((item, index) => ({
                index,
                ...item
            }))
        },
        showPackageDialog(packageData) {
            this.currentPackage = packageData

            Promise.all([
                this.$store.dispatch('calculations/getPackageBoxTypes'),
                this.$store.dispatch('calculations/getPackagesTypes')
            ])
                .then(responses => {
                    this.boxTypes = responses[0].data.results
                    this.packageTypes = responses[1].data.results
                    this.packageDialog = true
                })
                .catch(error => errorHandler(error))
        },
        closePackageDialog() {
            setTimeout(() => {
                this.currentPackage = null
            }, 500)
            this.packageDialog = false
        },
        savePackage(packageData, isNewPackage) {
            this.packageDialog = false

            if (!isNewPackage) {
                const index = this.boxesInfo.findIndex(item => item.index == packageData.index)

                this.boxesInfo.splice(index, 1, packageData)
            } else {
                this.boxesInfo.push(packageData)
                this.indexedParcelBoxesInfo()
            }

            this.closePackageDialog()
        },
        showConfirmDeletePackageDialog(packageData) {
            this.currentPackage = packageData
            this.confirmDeletePackageDialog = true
        },
        showConfirmDeletePalletDialog(boxesInfo, boxIndex) {
            this.currentPallet = {
                boxesInfo: boxesInfo,
                index: boxIndex
            }
            this.confirmDeletePalletDialog = true
        },
        closeConfirmDeletePackageDialog() {
            setTimeout(() => {
                this.currentPackage = null
            }, 500)
            this.confirmDeletePackageDialog = false
        },
        closeConfirmDeletePalletDialog() {
            this.currentPallet = null
            this.confirmDeletePalletDialog = false
        },
        deletePackage(packageData) {
            const index = this.boxesInfo.findIndex(item => item.index == packageData.index)

            this.boxesInfo.splice(index, 1)

            this.indexedParcelBoxesInfo()

            this.closeConfirmDeletePackageDialog()
        },
        deletePallet() {
            this.currentPallet.boxesInfo.splice(this.currentPallet.index, 1)
            this.calculateTotalAmountUsed()
            this.closeConfirmDeletePalletDialog()
        }, 
        showCreateShipmentProcessReportDialog() {
            this.actionLoading.createShipmentProcessReport = true
            this.$store.dispatch('reports/getReportProcesses', this.workspaceId)
                .then(response => {
                    this.reportProcesses = response.data.results
                    this.createShipmentProcessReportDialog = true
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.createShipmentProcessReport = false
                })

            addSentryBreadcrumb('ui.action', 'Raportuj czynność')
        },
        closeCreateShipmentProcessReportDialog() {
            this.createShipmentProcessReportDialog = false
        },
        packageTypeName(packageType) {
            return packageType === 'PALET'? 'Ilość palet' : 'Ilość kartonów'
        }
    },
}
</script>
